import axios from "axios";

/**
 * Единый API-клиент админки.
 *
 * Контракт (docs/ADMIN_API.md):
 * - успех:  { data, meta? }, создание — HTTP 201
 * - ошибка: { error: { message, code, details } } + реальный HTTP-статус
 */

const BASE_URL =
  process.env.REACT_APP_API_URL || "https://florapoint.su/api.admin/";

export class ApiError extends Error {
  constructor({ message, code, status, details }) {
    super(message);
    this.name = "ApiError";
    this.code = code || "UNKNOWN";
    this.status = status || 0;
    this.details = details ?? null;
  }
}

export const axiosClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosClient.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const payload = error?.response?.data?.error;

    return Promise.reject(
      new ApiError({
        message:
          payload?.message ||
          (status
            ? `Ошибка сервера (${status})`
            : "Нет соединения с сервером"),
        code: payload?.code || (status ? "INTERNAL_ERROR" : "NETWORK_ERROR"),
        status,
        details: payload?.details,
      })
    );
  }
);

/** GET, возвращает только data */
export async function apiGet(url, params) {
  const res = await axiosClient.get(url, { params });
  return res.data?.data;
}

/**
 * GET списка, возвращает { items, meta }.
 * meta: { page, pageSize, total, totalPages } — только у списков.
 */
export async function apiGetList(url, params) {
  const res = await axiosClient.get(url, { params });
  const body = res.data || {};
  return {
    items: Array.isArray(body.data) ? body.data : [],
    meta: body.meta || null,
  };
}

/** POST, возвращает data (201 при создании) */
export async function apiPost(url, body, config) {
  const res = await axiosClient.post(url, body, config);
  return res.data?.data;
}

/** PUT, возвращает data */
export async function apiPut(url, body) {
  const res = await axiosClient.put(url, body);
  return res.data?.data;
}

/** DELETE, возвращает data */
export async function apiDelete(url, params) {
  const res = await axiosClient.delete(url, { params });
  return res.data?.data;
}

/** Человекочитаемое сообщение из любой ошибки */
export function getErrorMessage(error, fallback = "Что-то пошло не так") {
  if (error instanceof ApiError) return error.message;
  return error?.message || fallback;
}
